// Sub-tabs (REQ-237, ADR-115, NAV-042): the second row of link tabs under a shell's section tabs,
// one URL per sub-tab, and the "unsaved changes" guard a dirty sub-tab raises when the person
// leaves it.
//
// The convention every library screen with sub-tabs follows (M1 builds against it):
//
//   1. The screen exports its table, `SUBTABS: readonly SubTabDef<S>[]`, in display order. Both
//      shells render that one table, so they show the same sub-tabs in the same order.
//   2. The screen takes two props from its shell, and never reads the URL (REQ-201):
//        `subtab: string | undefined`  the route's sub-tab segment; undefined at the bare section URL
//        `subtabHref(id: string): string`  the shell's URL of one of this screen's sub-tabs
//      The billing admin maps them under `/customers/:orgId/<tab>/<subtab>`, the proxy under
//      `/admin/<section>/<subtab>`.
//   3. At the bare URL (`subtab` undefined) the screen shows `firstVisible(SUBTABS, scope)`.
//      What an unknown or hidden `subtab` shows is the screen's decision (Prompt logs explains why
//      it is absent, REQ-244); the tab row never lists it.
//   4. Each sub-tab is a page of its own: its own load, error and 412 states, its own Save writing
//      only the paths it owns, and `useUnsavedGuard(dirty)` while its draft differs from what was
//      loaded.
//
// Example, inside a library screen:
//
//   export const PEOPLE_SUBTABS: readonly SubTabDef[] = [
//     { id: "users", label: "Users", visible: () => true },
//     { id: "teams", label: "Teams", visible: () => true },
//     { id: "invites", label: "Invites", visible: (me) => me !== null },
//   ];
//   export function PeopleScreen({ subtab, subtabHref }: { subtab: string | undefined; subtabHref(id: string): string }) {
//     const me = useScopeMe();
//     const current = subtab ?? firstVisible(PEOPLE_SUBTABS, me);
//     return (
//       <>
//         <SubTabs tabs={PEOPLE_SUBTABS} scope={me} current={current} href={subtabHref} label="People" />
//         {current === "users" && <UsersList />}
//         …
//       </>
//     );
//   }
import { useEffect } from "preact/hooks";
import type { ScopeMe } from "../scope/scope";
import { isPlainLeftClick } from "../host/host";
import { LinkTabs } from "./page";

/**
 * One sub-tab of a screen's table.
 *
 * - `id`: the URL segment (`…/<section>/<id>`): lower-case, hyphenated, stable; never a uuid.
 * - `label`: what the tab reads, in sentence case ("API keys").
 * - `visible(scope)`: whether the principal may open it. A sub-tab it may not open is absent from
 *   the row, never disabled (REQ-237). Pure and synchronous: it reads only `scope`.
 *
 * `S` is what the predicate reads: by default the library's signed-in principal (`ScopeMe`, or
 * null when nobody is signed in). A screen whose visibility needs more (the org's prompt logging,
 * say) declares its own scope type and passes a value of it to `SubTabs` and `firstVisible`.
 */
export interface SubTabDef<S = ScopeMe | null> {
  id: string;
  label: string;
  visible(scope: S): boolean;
}

/**
 * The id of the first sub-tab `scope` may open, in table order: what the section's bare URL shows.
 * Undefined when the principal may open none (the screen then shows its own "not available").
 *
 * firstVisible([{ id: "status", … visible: () => false }, { id: "providers", … visible: () => true }], me) === "providers"
 */
export function firstVisible<S>(defs: readonly SubTabDef<S>[], scope: S): string | undefined {
  return defs.find((def) => def.visible(scope))?.id;
}

export interface SubTabsProps<S> {
  /** The screen's whole table, in display order; the row lists only those `scope` may open. */
  tabs: readonly SubTabDef<S>[];
  /** What `visible` reads. */
  scope: S;
  /**
   * The sub-tab shown, marked `aria-current="page"`. Undefined (the bare section URL) marks the
   * first visible one; an id the row does not list marks none.
   */
  current: string | undefined;
  /** The shell's URL of a sub-tab (the screen's `subtabHref`). */
  href(id: string): string;
  /** The row's accessible name: the section's name ("Models"). */
  label: string;
}

/**
 * The sub-tab row: `LinkTabs` at the sub level (the `tabs-sub` style: smaller, under the section
 * tabs), one link per visible sub-tab, each navigating through the Host. Renders nothing when no
 * sub-tab is visible, and nothing when only one is (a single tab is not a choice).
 */
export function SubTabs<S>({ tabs, scope, current, href, label }: SubTabsProps<S>) {
  const visible = tabs.filter((tab) => tab.visible(scope));
  if (visible.length < 2) return null;
  const marked = current ?? (visible[0] as SubTabDef<S>).id;
  return <LinkTabs level="sub" label={label} current={marked} tabs={visible.map((tab) => ({ id: tab.id, label: tab.label, href: href(tab.id) }))} />;
}

/** What the guard asks before an in-app link leaves a dirty sub-tab. */
export const UNSAVED_CHANGES_PROMPT = "You have unsaved changes. Leave and discard them?";

function leavingLink(event: MouseEvent): HTMLAnchorElement | null {
  if (!isPlainLeftClick(event)) return null;
  const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
  if (!(anchor instanceof HTMLAnchorElement) || anchor.target === "_blank" || anchor.hasAttribute("download")) return null;
  const to = new URL(anchor.href, location.href);
  if (to.origin !== location.origin) return null;
  return to.pathname === location.pathname && to.search === location.search ? null : anchor;
}

/**
 * While `dirty` is true, leaving the page asks first:
 *
 * - closing the tab, reloading or typing another address: the browser's own "leave site?" prompt
 *   (`beforeunload`);
 * - clicking an in-app link to another address (another sub-tab, another section, any `Link`):
 *   `window.confirm(UNSAVED_CHANGES_PROMPT)`; Cancel keeps the person where they are, OK follows
 *   the link and the draft is lost.
 *
 * Not guarded: a link to the current address (hash-only or the same path and query), a modified
 * or middle click and `target="_blank"` (the page stays open), back/forward (the history has
 * already moved when the browser says so), and a screen's own `host.navigate` call after a save
 * (the screen knows it is clean). With `dirty` false the hook listens to nothing.
 *
 * useUnsavedGuard(JSON.stringify(draft) !== JSON.stringify(loaded));
 */
export function useUnsavedGuard(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers show the prompt only when returnValue is set.
      event.returnValue = "";
    };
    // Capture, so the question comes before the Link's own handler navigates.
    const onClick = (event: MouseEvent) => {
      if (leavingLink(event) === null || window.confirm(UNSAVED_CHANGES_PROMPT)) return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [dirty]);
}
